import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'

import {
  normalizeLanguageCodes,
  type VenueLanguageCode,
} from '@/lib/venue-languages'
import {
  getServiceInstanceIdFromSearch,
  resolveActiveServiceInstanceId,
  resolveFundraisingScopedServiceInstanceId,
  resolveWorkspaceActiveServiceInstanceId,
} from '@/lib/service-instance'
import { useAuthStore } from '@/stores/auth-store'
import { venueApi } from '@/services/venue'

type EditingLanguageContextValue = {
  serviceInstanceId: string
  supportedLanguages: VenueLanguageCode[]
  primaryLanguage: VenueLanguageCode
  editorLanguage: VenueLanguageCode
  setEditorLanguage: (language: VenueLanguageCode) => void
  loading: boolean
  refresh: () => Promise<void>
}

const EditingLanguageContext = createContext<EditingLanguageContextValue | null>(null)

const GLOBAL_STORAGE_KEY = 'portal.editingLanguage'
const storageKeyForSi = (serviceInstanceId: string) => `portal.editingLanguage.${serviceInstanceId}`

const readStorage = (key: string): VenueLanguageCode | null => {
  if (typeof window === 'undefined') return null
  try {
    const value = String(window.localStorage.getItem(key) || '').trim().toLowerCase()
    return value || null
  } catch {
    return null
  }
}

const writeStorage = (key: string, language: VenueLanguageCode) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, language)
  } catch {
    // ignore quota / private mode
  }
}

const readStoredLanguage = (serviceInstanceId: string): VenueLanguageCode | null => {
  if (serviceInstanceId) {
    const scoped = readStorage(storageKeyForSi(serviceInstanceId))
    if (scoped) return scoped
  }
  return readStorage(GLOBAL_STORAGE_KEY)
}

const writeStoredLanguage = (serviceInstanceId: string, language: VenueLanguageCode) => {
  writeStorage(GLOBAL_STORAGE_KEY, language)
  if (serviceInstanceId) {
    writeStorage(storageKeyForSi(serviceInstanceId), language)
  }
  try {
    if (serviceInstanceId) {
      window.sessionStorage.removeItem(storageKeyForSi(serviceInstanceId))
    }
  } catch {
    // ignore
  }
}

function useResolvedServiceInstanceId(): string {
  const location = useLocation()
  const accessToken = useAuthStore((state) => state.accessToken)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)

  return useMemo(() => {
    const fromUrl = getServiceInstanceIdFromSearch(location.search)
    if (fromUrl) return fromUrl

    if (location.pathname.startsWith('/fundraising')) {
      return resolveFundraisingScopedServiceInstanceId({
        search: location.search,
        post: currentPost,
        availablePosts,
        storedServiceInstanceId,
      }) || resolveActiveServiceInstanceId({
        search: location.search,
        post: currentPost,
        currentBranch,
        availableBranches,
        storedServiceInstanceId,
        token: accessToken,
      }) || ''
    }

    return resolveWorkspaceActiveServiceInstanceId({
      search: location.search,
      post: currentPost,
      currentBranch,
      availableBranches,
      storedServiceInstanceId,
      token: accessToken,
    }) || resolveActiveServiceInstanceId({
      search: location.search,
      post: currentPost,
      currentBranch,
      availableBranches,
      storedServiceInstanceId,
      token: accessToken,
    }) || ''
  }, [
    location.pathname,
    location.search,
    currentPost,
    availablePosts,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
    accessToken,
  ])
}

type EditingLanguageProviderProps = {
  children: ReactNode
  serviceInstanceId?: string
}

export function EditingLanguageProvider({
  children,
  serviceInstanceId: serviceInstanceIdProp,
}: EditingLanguageProviderProps) {
  const resolvedFromRoute = useResolvedServiceInstanceId()
  const serviceInstanceId = String(serviceInstanceIdProp || resolvedFromRoute || '').trim()
  const serviceInstanceIdRef = useRef(serviceInstanceId)
  const [supportedLanguages, setSupportedLanguages] = useState<VenueLanguageCode[]>(['en'])
  const [editorLanguage, setEditorLanguageState] = useState<VenueLanguageCode>(() => (
    readStoredLanguage(serviceInstanceId) || 'en'
  ))
  const [loading, setLoading] = useState(true)

  const applyLanguages = useCallback((languages: VenueLanguageCode[], si: string) => {
    const next = normalizeLanguageCodes(languages, ['en'])
    setSupportedLanguages(next)

    const siChanged = serviceInstanceIdRef.current !== si
    serviceInstanceIdRef.current = si

    setEditorLanguageState((current) => {
      if (!siChanged && current && next.includes(current)) {
        return current
      }
      const stored = readStoredLanguage(si)
      if (stored && next.includes(stored)) {
        return stored
      }
      if (current && next.includes(current)) {
        return current
      }
      return next[0] || 'en'
    })
  }, [])

  const refresh = useCallback(async () => {
    if (!serviceInstanceId) {
      applyLanguages(['en'], '')
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const terminology = await venueApi.getTerminology(serviceInstanceId)
      const languages = normalizeLanguageCodes(
        terminology?.supported_languages?.length
          ? terminology.supported_languages
          : [terminology?.primary_language, terminology?.secondary_language].filter(Boolean),
        ['en'],
      )
      applyLanguages(languages, serviceInstanceId)
    } catch {
      applyLanguages(['en'], serviceInstanceId)
    } finally {
      setLoading(false)
    }
  }, [applyLanguages, serviceInstanceId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const setEditorLanguage = useCallback((language: VenueLanguageCode) => {
    const code = String(language || '').trim().toLowerCase()
    if (!code) return
    setEditorLanguageState(code)
    writeStoredLanguage(serviceInstanceId, code)
  }, [serviceInstanceId])

  const value = useMemo<EditingLanguageContextValue>(() => ({
    serviceInstanceId,
    supportedLanguages,
    primaryLanguage: supportedLanguages[0] || 'en',
    editorLanguage: supportedLanguages.includes(editorLanguage)
      ? editorLanguage
      : (supportedLanguages[0] || 'en'),
    setEditorLanguage,
    loading,
    refresh,
  }), [
    serviceInstanceId,
    supportedLanguages,
    editorLanguage,
    setEditorLanguage,
    loading,
    refresh,
  ])

  return (
    <EditingLanguageContext.Provider value={value}>
      {children}
    </EditingLanguageContext.Provider>
  )
}

export function useEditingLanguage(): EditingLanguageContextValue {
  const ctx = useContext(EditingLanguageContext)
  if (!ctx) {
    return {
      serviceInstanceId: '',
      supportedLanguages: ['en'],
      primaryLanguage: 'en',
      editorLanguage: 'en',
      setEditorLanguage: () => undefined,
      loading: false,
      refresh: async () => undefined,
    }
  }
  return ctx
}
